import { Controller } from "react-hook-form";
import { useEffect } from "react";
import FormInput from "../../components/ui/FormInput";
import InfiniteScrollSelect from "../../components/ui/InfiniteScrollSelect";
import RichTextEditor from "../../components/ui/RichTextEditor";
import EventServicesSection from "./EventServiceSection";
import { PROMO_RESTRICTION_TYPES } from "../../rules/promo";

interface Props {
  control: any;
  watch: any;
  setValue: any;
  maxUsage: number;
  defaultValues?: PromoCode;
}

export default function CreatePromoCode(props: Props) {
  const { control, watch, setValue, maxUsage = 0, defaultValues } = props;

  const scope = watch("scope");
  const selectedEvent = watch("event");

  // Clear unrelated fields when scope changes
  useEffect(() => {
    if (scope !== "EVENT") {
      setValue("event", null);
      setValue("items", []);
    }
    if (scope !== "SPECIALS") {
      setValue("offer", null);
    }
    if (scope !== "PLACE") {
      setValue("place", null);
    }
    // Clear restrictionType when scope is not GLOBAL
    if (scope !== "GLOBAL") {
      setValue("restrictionType", "");
    }
  }, [scope, setValue]);

  return (
    <div className="row form">
      {/* NAME */}
      <div className="col-md-6 mt-4">
        <FormInput
          name="name"
          label={
            <>
              Name <span className="text-danger">*</span>
            </>
          }
          control={control}
          defaultValue={defaultValues?.name}
        />
      </div>

      {/* DISCOUNT */}
      <div className="col-md-6 mt-4">
        <FormInput
          name="discount"
          type="number"
          min={0}
          label={
            <>
              Discount (%) <span className="text-danger">*</span>
            </>
          }
          control={control}
          defaultValue={defaultValues?.discount?.toString()}
        />
      </div>

      {/* MAX USES */}
      <div className="col-md-6 mt-4">
        <FormInput
          name="maxUses"
          type="number"
          min={0}
          label={
            <>
              Max usage <span className="text-danger">*</span>
            </>
          }
          control={control}
          defaultValue={defaultValues?.maxUses}
        />
      </div>

      {/* SCOPE */}
      <div className="col-md-6 mt-4">
        <Controller
          name="scope"
          control={control}
          defaultValue={defaultValues?.scope || "GLOBAL"}
          render={({ field, fieldState }) => (
            <>
              <label className="d-block">
                Type de code promo <span className="text-danger">*</span>
              </label>
              <select className="form-select" {...field}>
                <option value="GLOBAL">Global</option>
                <option value="EVENT">Événement (lié)</option>
                <option value="SPECIALS">Offre (lié)</option>
                <option value="PLACE">Établissement (lié)</option>
              </select>
              <ErrorMessage error={fieldState.error?.message} />
            </>
          )}
        />
      </div>

      {/* RESTRICTION TYPE (Only for GLOBAL scope) */}
      {scope === "GLOBAL" && (
        <div className="col-md-6 mt-4">
          <Controller
            name="restrictionType"
            control={control}
            defaultValue={defaultValues?.restrictionType || ""}
            render={({ field, fieldState }) => (
              <>
                <label className="d-block">
                  Restreindre à <span className="text-danger">*</span>
                </label>
                <select className="form-select" {...field}>
                  {PROMO_RESTRICTION_TYPES.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <small className="text-muted">
                  {field.value === ""
                    ? "Le code s'appliquera à tous les événements, offres et Établissements"
                    : `Le code s'appliquera uniquement aux ${field.value?.toLowerCase()}s`}
                </small>
                <ErrorMessage error={fieldState.error?.message} />
              </>
            )}
          />
        </div>
      )}

      {/* EVENT SELECT (Only for linked EVENT scope) */}
      {scope === "EVENT" && (
        <div className="mt-4 col-md-6">
          <Controller
            name="event"
            control={control}
            defaultValue={defaultValues?.event}
            render={({ field, fieldState }) => (
              <>
                <label>Event spécifique</label>
                <InfiniteScrollSelect
                  url={{ path: "events/ours" }}
                  getOptionLabel={(o: any) => o.name}
                  getOptionValue={(o: any) => o.id}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Sélectionner un événement"
                />
                <ErrorMessage error={fieldState.error?.message} />
              </>
            )}
          />
        </div>
      )}

      {/* OFFER SELECT (Only for linked SPECIALS scope) */}
      {scope === "SPECIALS" && (
        <div className="mt-4 col-md-6">
          <Controller
            name="offer"
            control={control}
            defaultValue={defaultValues?.offer}
            render={({ field, fieldState }) => (
              <>
                <label>Offer spécifique</label>
                <InfiniteScrollSelect
                  url={{ path: "specials/all" }}
                  getOptionLabel={(o: any) => o.title}
                  getOptionValue={(o: any) => o.id}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Sélectionner une offre"
                />
                <ErrorMessage error={fieldState.error?.message} />
              </>
            )}
          />
        </div>
      )}

      {/* PLACE SELECT (Only for linked PLACE scope) */}
      {scope === "PLACE" && (
        <div className="mt-4 col-md-6">
          <Controller
            name="place"
            control={control}
            defaultValue={defaultValues?.place}
            render={({ field, fieldState }) => (
              <>
                <label>Place spécifique</label>
                <InfiniteScrollSelect
                  url={{ path: "places" }}
                  getOptionLabel={(o: any) => o.name}
                  getOptionValue={(o: any) => o.id}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Sélectionner un Établissement"
                />
                <ErrorMessage error={fieldState.error?.message} />
              </>
            )}
          />
        </div>
      )}

      {/* SERVICES SECTION (Only for linked EVENT scope) */}
      {scope === "EVENT" && (
        <EventServicesSection
          control={control}
          event={selectedEvent}
          maxUsage={maxUsage}
        />
      )}

      {/* ACTIVE */}
      <div className="col-md-6 mt-4">
        <Controller
          name="isActive"
          control={control}
          defaultValue={defaultValues?.isActive ?? true}
          render={({ field, fieldState }) => (
            <div>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={field.value}
                  onChange={field.onChange}
                />
                <label htmlFor="isActive">Actif</label>
              </div>
              <ErrorMessage error={fieldState.error?.message} />
            </div>
          )}
        />
      </div>

      {/* DESCRIPTION */}
      <div className="mt-4">
        <Controller
          name="description"
          control={control}
          defaultValue={defaultValues?.description as string}
          render={({ field, fieldState }) => (
            <>
              <label>Description</label>
              <RichTextEditor {...field} />
              <ErrorMessage error={fieldState.error?.message} />
            </>
          )}
        />
      </div>
    </div>
  );
}

export function ErrorMessage({ error }: { error?: string }) {
  if (!error) return null;
  return <div style={{ color: "red", fontSize: "0.85rem" }}>{error}</div>;
}
